import { useEffect, useState } from "react";
import {
  useAxiosInstance,
  useAxiosBaseURL,
} from "../../../common/hooks/useAxiosInstance";
import type { AxiosInstance } from "axios";

export interface BaseHookReturn<T> {
  data: T | null;
  loading: boolean;
  error: any;
}

export interface UseApiRequestOptions {
  logLabel?: string;
}

export const useApiRequest = <
  TData,
  TDeps extends readonly unknown[] = readonly unknown[]
>(
  deps: TDeps,
  execute: (axios: AxiosInstance, baseURL: string) => Promise<TData>,
  options?: UseApiRequestOptions,
): BaseHookReturn<TData> => {
  const axios = useAxiosInstance();
  const baseURL = useAxiosBaseURL();

  const [data, setData] = useState<TData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (!axios || !baseURL) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    execute(axios as AxiosInstance, baseURL as string)
      .then((result) => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          if (options?.logLabel) {
            // eslint-disable-next-line no-console
            console.warn(`${options.logLabel} failed: `, err);
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axios, baseURL, ...deps]);

  return { data, loading, error };
};
